/**
 * Tax Resolution — pure domain logic for resolving the default tax rate
 * for an expense entry based on receipt AI data, entry type, and currency.
 *
 * Extracted from ChildEntryForm to enable independent testing and reuse
 * across capture surfaces (SmartTask, SmartAutomation, ExpensesManagementModal).
 */

import { parseTaxRateValue } from '@/lib/finance/taxRates';
import type { ReceiptAiPayload } from '@/lib/receiptAiAnalysis';

const STANDARD_VAT_RATES = [0, 15];

/**
 * Validate and correct the OCR-extracted tax_amount.
 *
 * OCR models sometimes pick up the wrong number as tax_amount (e.g. the "Vat %"
 * line value instead of the "Vat Total" line value). If the extracted tax_amount
 * doesn't reconcile with total - subtotal, use the implied tax instead.
 *
 * Also tries to extract the correct tax amount from raw text (e.g. "Vat Total 147.23").
 */
export function correctReceiptTaxAmount(
  taxAmount: number | null,
  subtotal: number | null,
  totalAmount: number | null,
  rawText: string | undefined | null,
): number | null {
  if (taxAmount == null) return taxAmount;

  // If we have subtotal + total, validate against implied tax
  if (subtotal != null && totalAmount != null && subtotal > 0 && totalAmount > 0) {
    const impliedTax = Math.round((totalAmount - subtotal) * 100) / 100;
    if (Math.abs(taxAmount - impliedTax) > 1) {
      // Extracted tax doesn't reconcile — try raw text first, then use implied tax
      if (rawText) {
        const vatTotalMatch = rawText.toUpperCase().match(/VAT\s*TOTAL\s*:?\s*(\d+\.\d{2})/);
        if (vatTotalMatch) {
          const textTax = parseFloat(vatTotalMatch[1]);
          if (textTax > 0 && Math.abs(textTax - impliedTax) <= 1) {
            return textTax;
          }
        }
      }
      return impliedTax > 0 ? impliedTax : taxAmount;
    }
  }

  return taxAmount;
}

/**
 * Snap near-standard rates (e.g. 15.01% from receipt rounding) to the exact
 * standard rate.
 */
export function snapToStandardVatRate(rate: number): number {
  for (const std of STANDARD_VAT_RATES) {
    if (Math.abs(rate - std) <= 0.5) return std;
  }
  return rate;
}

/**
 * Detect if a receipt has mixed VAT treatment (some items vatable, some non-vatable).
 * This produces a blended rate (e.g. 7.43%) that is not a real tax rate.
 *
 * Looks for indicators in the raw text like:
 *   "12.00 Vat %: 15 Vat Total: 147.23"
 *   "15.00 * Non-Vatable"
 *   "Vat % Vat Total\n12.00 15 147.23\n15.00 = Non-Vatabi 0.00"
 *
 * Handles OCR variations:
 *   - VAT rate number on a separate line from the "VAT %" label (table layout)
 *   - OCR truncation of "Non-Vatable" to "Non-Vatabi", "Non-Vatab", etc.
 */
export function hasMixedVatTreatment(rawText: string | undefined | null): boolean {
  if (!rawText) return false;
  const upper = rawText.toUpperCase();
  // Match "VAT %" label followed by 15 anywhere nearby (same line or next few chars/lines)
  const hasVatRate = /VAT\s*%[\s\S]{0,20}?15\b/.test(upper);
  // Match "Non-Vatable" or OCR truncations like "Non-Vatabi", "Non-Vatab", etc.
  const hasNonVatable = /NON[-\s]?VATAB/.test(upper);
  return hasVatRate && hasNonVatable;
}

/**
 * When a receipt has mixed VAT treatment, the computed rate (tax_amount / subtotal)
 * is a blended rate across vatable and non-vatable items. This is not a real tax
 * rate — the actual VAT rate is 15% (or whatever the receipt states).
 *
 * Returns the real VAT rate if mixed treatment is detected, otherwise returns
 * the input rate unchanged.
 */
function resolveMixedVatRate(rate: number, rawText: string | undefined | null): number {
  if (hasMixedVatTreatment(rawText)) {
    // Try to extract the stated VAT rate from a single-line pattern (e.g. "Vat %: 15")
    const match = rawText!.toUpperCase().match(/VAT\s*%\s*:?\s*(\d+(?:\.\d+)?)\s/);
    if (match) {
      const statedRate = parseFloat(match[1]);
      if (statedRate > 0 && statedRate <= 30) return statedRate;
    }
    // hasMixedVatTreatment already confirmed 15 is present near "VAT %", so default to 15
    return 15;
  }

  // Numeric detection: if the rate is non-standard (not 0 or 15) and the raw text
  // contains VAT keywords, it's likely a blended rate from mixed VAT treatment.
  // In South Africa the only VAT rates are 0% and 15%, so any other rate is blended.
  if (rawText && rate > 0 && rate <= 30) {
    const isNonStandardRate = STANDARD_VAT_RATES.every(std => Math.abs(rate - std) > 0.5);
    const hasVatKeywords = /VAT\s*(?:%|TOTAL)/i.test(rawText);
    if (isNonStandardRate && hasVatKeywords) {
      return 15;
    }
  }

  return rate;
}

/**
 * Mixed VAT split result — the Hubdoc-style approach of splitting a receipt
 * with mixed VAT treatment into two summary lines: taxable and non-taxable.
 *
 * Instead of trying to detect which individual line items are vatable (which
 * neither Dext nor Hubdoc attempt), we compute the taxable subtotal from the
 * receipt's stated tax amount and VAT rate, then derive the non-taxable portion.
 */
export interface MixedVatSplit {
  /** The actual VAT rate (e.g. 15) */
  vatRate: number;
  /** Subtotal of vatable items */
  taxableSubtotal: number;
  /** Subtotal of non-vatable items (receiptSubtotal - taxableSubtotal) */
  nonTaxableSubtotal: number;
  /** The receipt's stated tax amount */
  taxAmount: number;
  /** Whether the line item prices include VAT */
  taxInclusive: boolean;
  /** Total number of line items on the receipt (for reference) */
  itemCount: number;
}

/**
 * Detect and compute the mixed VAT split for a receipt.
 *
 * Returns null if the receipt does not have mixed VAT treatment or if the
 * numbers don't add up (e.g. taxable subtotal exceeds receipt subtotal).
 *
 * Example:
 *   Receipt: subtotal=1982.52, tax_amount=147.23, VAT rate=15%
 *   VAT-exclusive: taxableSubtotal = 147.23 / 0.15 = 981.53
 *   nonTaxableSubtotal = 1982.52 - 981.53 = 1000.99
 */
export function detectMixedVatSplit(
  receiptResult: ReceiptAiPayload | null,
  vatRate: number,
  taxInclusive: boolean,
): MixedVatSplit | null {
  if (!receiptResult) return null;
  if (vatRate <= 0 || vatRate > 30) return null;

  const receiptSubtotal = receiptResult.subtotal != null ? Number(receiptResult.subtotal) : null;
  const receiptTotalAmount = receiptResult.total_amount != null ? Number(receiptResult.total_amount) : null;
  const receiptTaxAmountRaw = receiptResult.tax_amount != null ? Number(receiptResult.tax_amount) : null;
  const itemCount = receiptResult.line_items?.length ?? 0;

  if (!receiptSubtotal || receiptSubtotal <= 0) return null;

  // Correct the OCR-extracted tax_amount before using it
  const receiptTaxAmount = correctReceiptTaxAmount(
    receiptTaxAmountRaw, receiptSubtotal, receiptTotalAmount, receiptResult.raw_text,
  );

  if (!receiptTaxAmount || receiptTaxAmount <= 0) return null;

  // Check for mixed VAT treatment: either text-based detection or numeric detection.
  // Numeric detection: if the effective rate (taxAmount / subtotal) is non-standard
  // (not within 0.5 of 0 or 15) and the raw text contains VAT-related keywords,
  // treat it as mixed VAT. This catches receipts where OCR didn't capture "Non-Vatable"
  // text but the numbers clearly show mixed treatment.
  const hasTextIndicator = hasMixedVatTreatment(receiptResult.raw_text);
  if (!hasTextIndicator) {
    const effectiveRate = Math.round((receiptTaxAmount / receiptSubtotal) * 100 * 100) / 100;
    const isNonStandardRate = STANDARD_VAT_RATES.every(std => Math.abs(effectiveRate - std) > 0.5);
    const hasVatKeywords = receiptResult.raw_text != null &&
      /VAT\s*(?:%|TOTAL)/i.test(receiptResult.raw_text);
    if (!isNonStandardRate || !hasVatKeywords) return null;
  }

  let taxableSubtotal: number;
  if (taxInclusive) {
    // taxAmount = taxableSubtotal * vatRate / (100 + vatRate)
    taxableSubtotal = Math.round((receiptTaxAmount / (vatRate / (100 + vatRate))) * 100) / 100;
  } else {
    // taxAmount = taxableSubtotal * (vatRate / 100)
    taxableSubtotal = Math.round((receiptTaxAmount / (vatRate / 100)) * 100) / 100;
  }

  const nonTaxableSubtotal = Math.round((receiptSubtotal - taxableSubtotal) * 100) / 100;

  // Sanity checks
  if (taxableSubtotal <= 0 || taxableSubtotal > receiptSubtotal) return null;
  if (nonTaxableSubtotal < 0) return null;

  return {
    vatRate,
    taxableSubtotal,
    nonTaxableSubtotal,
    taxAmount: receiptTaxAmount,
    taxInclusive,
    itemCount,
  };
}

export interface TaxResolutionInput {
  selectedEntryType: string | null;
  receiptResult: ReceiptAiPayload | null;
  detectedCurrency: string;
  preferredDefaultTaxRate?: { rate: string } | null;
}

export interface TaxResolutionResult {
  rate: number;
  source: 'fuel_exempt' | 'toll_exempt' | 'zero_rated_vendor' | 'receipt_subtotal' | 'receipt_implied' | 'receipt_zero_tax' | 'currency_default' | 'preferred' | 'fallback';
}

// ============================================================================
// VAT TREATMENT DETECTION
// ============================================================================

/**
 * Vendors/categories that are zero-rated or VAT-exempt in South Africa.
 * Fuel is zero-rated for input tax purposes (the purchaser cannot claim input VAT).
 * Tolls are typically zero-rated. Some vendors are non-VAT vendors.
 */
const ZERO_RATED_KEYWORDS: string[] = [
  // Fuel
  'fuel', 'petrol', 'diesel', 'shell', 'bp', 'engen', 'caltex', 'sasol',
  'forecourt', 'garage', 'fill up', 'unleaded', 'diesel 50ppm',
  // Tolls
  'toll', 'gantry', 'e-tag', 'sanral', 'e-toll',
];

/**
 * Vendors/categories where prices are typically VAT-inclusive (the displayed
 * price includes VAT). This is the default for most South African retail receipts.
 */
const VAT_INCLUSIVE_KEYWORDS: string[] = [
  'restaurant', 'osteria', 'bar', 'pub', 'grill', 'cafe', 'coffee',
  'shop', 'store', 'supermarket', 'checkers', 'pick n pay', 'woolworths',
  'spar', 'shoprite', 'game', 'makro', 'takealot', 'pharmacy', 'dischem',
  'clicks', 'hardware', 'builders', 'cashbuild', 'builders warehouse',
];

/**
 * Vendors/categories where prices are typically VAT-exclusive (the displayed
 * price excludes VAT, and VAT is added at checkout). This is common for
 * B2B suppliers, wholesalers, and service providers.
 */
const VAT_EXCLUSIVE_KEYWORDS: string[] = [
  'wholesale', 'supplier', 'distributor', 'manufacturer', 'b2b',
  'invoice', 'quotation', 'trade', 'bulk',
];

export interface VatTreatmentResult {
  /** The resolved tax rate (0 for zero-rated/exempt) */
  rate: number;
  /** Whether the unit prices on the receipt are VAT-inclusive */
  taxInclusive: boolean;
  /** Where the determination came from */
  source: string;
  /** Human-readable reason for the determination */
  reason: string;
}

/**
 * Detect the VAT treatment for a receipt/expense based on:
 * 1. Entry type (fuel → 0%, toll → 0%)
 * 2. Vendor name (known zero-rated vendors, VAT-inclusive vs exclusive vendors)
 * 3. Receipt data (subtotal vs total → VAT-inclusive detection)
 * 4. Line item data (per-line tax_rate from OCR)
 *
 * This is the seller's tax setup, not the client's default tax setup.
 */
export function detectVatTreatment(input: {
  selectedEntryType: string | null;
  receiptResult: ReceiptAiPayload | null;
  detectedCurrency: string;
  vendorName?: string | null;
  preferredDefaultTaxRate?: { rate: string } | null;
}): VatTreatmentResult {
  const { selectedEntryType, receiptResult, detectedCurrency, vendorName } = input;

  const entryType = String(selectedEntryType || '').toLowerCase();
  const vendor = String(vendorName || receiptResult?.vendor_name || '').toLowerCase();
  const category = String(receiptResult?.suggested_category || '').toLowerCase();
  const haystack = `${entryType} ${vendor} ${category}`;

  // ── 1. Entry type checks: fuel and toll are zero-rated ──
  if (entryType === 'fuel' || entryType === 'capture_fuel') {
    return {
      rate: 0,
      taxInclusive: false,
      source: 'fuel_exempt',
      reason: 'Fuel is zero-rated for input VAT in South Africa',
    };
  }

  if (entryType === 'toll' || entryType === 'capture_toll') {
    return {
      rate: 0,
      taxInclusive: false,
      source: 'toll_exempt',
      reason: 'Tolls are zero-rated for input VAT in South Africa',
    };
  }

  // ── 2. Zero-rated vendor/category detection ──
  for (const keyword of ZERO_RATED_KEYWORDS) {
    if (haystack.includes(keyword)) {
      // Check if this is actually a fuel/toll context, not just a keyword match
      // e.g. "Shell" as a vendor with entry_type "expense" (not "fuel") — still zero-rated for fuel
      const isFuelContext = ['fuel', 'petrol', 'diesel', 'shell', 'bp', 'engen', 'caltex', 'sasol', 'forecourt', 'garage', 'unleaded', 'diesel 50ppm'].some(k => haystack.includes(k));
      const isTollContext = ['toll', 'gantry', 'e-tag', 'sanral', 'e-toll'].some(k => haystack.includes(k));

      if (isFuelContext) {
        return {
          rate: 0,
          taxInclusive: false,
          source: 'zero_rated_vendor',
          reason: `Vendor/category "${vendor || category}" appears to be fuel — zero-rated for input VAT`,
        };
      }
      if (isTollContext) {
        return {
          rate: 0,
          taxInclusive: false,
          source: 'zero_rated_vendor',
          reason: `Vendor/category "${vendor || category}" appears to be a toll — zero-rated for input VAT`,
        };
      }
    }
  }

  // ── 3. Determine VAT-inclusive vs exclusive from receipt data ──
  const receiptSubtotal = receiptResult?.subtotal != null ? Number(receiptResult.subtotal) : null;
  const receiptTaxAmountRaw = receiptResult?.tax_amount != null ? Number(receiptResult.tax_amount) : null;
  const receiptTotalAmount = receiptResult?.total_amount != null ? Number(receiptResult.total_amount) : null;

  // Validate and correct the OCR-extracted tax_amount (e.g. OCR picked up "Vat %" value
  // instead of "Vat Total" value on Hokaai till slips)
  const receiptTaxAmount = correctReceiptTaxAmount(
    receiptTaxAmountRaw, receiptSubtotal, receiptTotalAmount, receiptResult?.raw_text,
  );

  // ── 3a. Check raw text for explicit VAT-inclusive indicators ──
  // Some receipts (e.g. Mr Price Sport) show both "TOTAL Before VAT" and
  // "TOTAL After VAT", with line items marked "* Includes VAT". The
  // subtotal + tax = total arithmetic would classify these as VAT-exclusive,
  // but the line item prices ARE VAT-inclusive. Check for explicit text
  // markers first to avoid misclassifying these receipts.
  const rawTextLower = String(receiptResult?.raw_text || '').toLowerCase();
  const hasInclusiveVatMarker = rawTextLower.includes('includes vat')
    || rawTextLower.includes('incl. vat')
    || rawTextLower.includes('incl vat')
    || rawTextLower.includes('vat inclusive')
    || /\*\s*includes?\s+vat/i.test(String(receiptResult?.raw_text || ''));

  // If the receipt has a subtotal and total, and total > subtotal, then the line items
  // are likely VAT-exclusive (subtotal = sum of line items before tax, total = subtotal + tax)
  // If the receipt has a subtotal and total, and total == subtotal, then no VAT was charged
  // If there's no subtotal but there's a total and tax_amount, the line items are likely
  // VAT-inclusive (the total is the sum of line items including tax)
  let taxInclusive = false;
  let rate = 0;
  let source = 'fallback';
  let reason = 'Default: VAT-exclusive at 15%';

  // If the raw text explicitly says prices include VAT, override the
  // subtotal/total arithmetic — the line items are VAT-inclusive.
  if (hasInclusiveVatMarker) {
    taxInclusive = true;
    // Still compute the rate from the receipt data if available
    if (receiptSubtotal && receiptSubtotal > 0 && receiptTaxAmount != null && receiptTaxAmount > 0) {
      let computedRate = Math.round((receiptTaxAmount / receiptSubtotal) * 100 * 100) / 100;
      computedRate = resolveMixedVatRate(computedRate, receiptResult?.raw_text);
      rate = snapToStandardVatRate(computedRate);
    } else {
      rate = 15;
    }
    source = 'receipt_text_marker';
    reason = `Receipt text indicates prices include VAT → VAT-inclusive at ${rate}%`;
    return { rate, taxInclusive, source, reason };
  }

  // Check if line items have per-line tax_rate from OCR
  const hasPerLineTaxRate = receiptResult?.line_items?.some(
    li => li.tax_rate != null && Number(li.tax_rate) > 0
  ) ?? false;

  // Universal VAT-inclusive signal: line items summing to the TOTAL means
  // prices already contain VAT. On genuine exclusive invoices items sum to
  // the subtotal (15% below total) so this can't misfire. Tolerance scales
  // with the total — weighed-item slips drift a few cents per line.
  const itemsSumAll = (receiptResult?.line_items ?? [])
    .reduce((acc, li) => acc + (Number(li.total) || 0), 0);
  const itemsCoverTolerance = Math.max(
    0.05,
    Math.round((receiptTotalAmount ?? receiptSubtotal ?? 0) * 0.005 * 100) / 100,
  );
  const itemsCoverTotal = itemsSumAll > 0 && receiptTotalAmount != null &&
    Math.abs(itemsSumAll - receiptTotalAmount) <= itemsCoverTolerance;

  // If receipt has subtotal + tax_amount → compute rate
  if (receiptSubtotal && receiptSubtotal > 0 && receiptTaxAmount != null && receiptTaxAmount > 0) {
    let computedRate = Math.round((receiptTaxAmount / receiptSubtotal) * 100 * 100) / 100;
    // Check for mixed VAT treatment (blended rate) before snapping
    computedRate = resolveMixedVatRate(computedRate, receiptResult?.raw_text);
    rate = snapToStandardVatRate(computedRate);
    if (rate > 0 && rate <= 30) {
      // If subtotal + tax = total, prices are VAT-exclusive
      // If subtotal = total (and tax is embedded), prices are VAT-inclusive
      if (receiptTotalAmount != null) {
        const expectedExclusive = Math.round((receiptSubtotal + receiptTaxAmount) * 100) / 100;
        // itemsCoverTotal (hoisted above) overrides the exclusive guess —
        // retail till slips land here via the net-total swap: subtotal +
        // tax = total satisfies the exclusive arithmetic yet the item
        // prices are inclusive.
        if (Math.abs(receiptTotalAmount - expectedExclusive) <= 0.02) {
          if (itemsCoverTotal) {
            taxInclusive = true;
            reason = `Line items sum to total → prices already VAT-inclusive at ${rate}%`;
          } else {
            taxInclusive = false;
            reason = `Receipt subtotal + tax = total → VAT-exclusive at ${rate}%`;
          }
          source = 'receipt_subtotal';
        } else if (Math.abs(receiptTotalAmount - receiptSubtotal) <= 0.02) {
          // Total equals subtotal — tax is embedded in the line item prices
          taxInclusive = true;
          source = 'receipt_subtotal';
          reason = `Receipt total = subtotal → VAT-inclusive at ${rate}%`;
        } else {
          // Ambiguous — check for embedded VAT before defaulting to
          // exclusive. Retail till slips (e.g. Burger King) print the
          // VAT-inclusive subtotal plus "VAT 15%: <embedded portion>" —
          // tax_amount = subtotal × rate/(100+rate) exactly.
          const embeddedVat = Math.round(receiptSubtotal * (rate / (100 + rate)) * 100) / 100;
          // Stronger signal that survives a bad tax_amount: if the line
          // items already sum to the printed subtotal, their prices are
          // the subtotal — nothing to add on top.
          const itemsSum = (receiptResult?.line_items ?? [])
            .reduce((acc, li) => acc + (Number(li.total) || 0), 0);
          // Inclusive slips: items sum = paid total (prices include VAT).
          // Exclusive slips reach here with total = subtotal + tax, so a
          // match against total_amount can't misfire on them.
          const itemsCover = itemsSum > 0 &&
            (Math.abs(itemsSum - receiptSubtotal) <= 0.05 ||
              (receiptTotalAmount != null && Math.abs(itemsSum - receiptTotalAmount) <= 0.05));
          if (Math.abs(receiptTaxAmount - embeddedVat) <= 0.05 || itemsCover) {
            taxInclusive = true;
            reason = itemsCover
              ? `Line items sum to printed total/subtotal → prices already VAT-inclusive at ${rate}%`
              : `Receipt tax = embedded VAT portion of subtotal → VAT-inclusive at ${rate}%`;
          } else {
            taxInclusive = false;
            reason = `Receipt has subtotal + tax → ${rate}% VAT (assumed exclusive)`;
          }
          source = 'receipt_subtotal';
        }
      } else {
        taxInclusive = false;
        source = 'receipt_subtotal';
        reason = `Receipt has subtotal + tax → ${rate}% VAT (assumed exclusive)`;
      }
    } else {
      rate = 15;
      taxInclusive = false;
      source = 'fallback';
      reason = `Computed rate ${rate}% out of range, defaulting to 15%`;
    }
  } else if (receiptSubtotal && receiptSubtotal > 0 && receiptTaxAmount == null && receiptTotalAmount && receiptTotalAmount > receiptSubtotal) {
    // Implied tax: total > subtotal, no explicit tax_amount
    const impliedTax = receiptTotalAmount - receiptSubtotal;
    rate = snapToStandardVatRate(Math.round((impliedTax / receiptSubtotal) * 100 * 100) / 100);
    if (rate > 0 && rate <= 30) {
      if (itemsCoverTotal) {
        taxInclusive = true;
        source = 'receipt_implied';
        reason = `Line items sum to total → prices already VAT-inclusive at ${rate}%`;
      } else {
        taxInclusive = false;
        source = 'receipt_implied';
        reason = `Implied tax from total - subtotal → ${rate}% VAT (exclusive)`;
      }
    } else {
      rate = 15;
      taxInclusive = false;
      source = 'fallback';
      reason = `Implied rate out of range, defaulting to 15%`;
    }
  } else if (receiptTaxAmount === 0 || (receiptTaxAmount != null && receiptTaxAmount === 0)) {
    // Explicitly zero tax
    rate = 0;
    taxInclusive = false;
    source = 'receipt_zero_tax';
    reason = 'Receipt explicitly shows zero tax';
  } else if (receiptSubtotal && receiptTotalAmount && Math.abs(receiptSubtotal - receiptTotalAmount) <= 0.02) {
    // Subtotal equals total — no VAT charged
    rate = 0;
    taxInclusive = false;
    source = 'receipt_zero_tax';
    reason = 'Receipt subtotal = total → no VAT charged';
  } else if (!receiptSubtotal && receiptTotalAmount && receiptTaxAmount && receiptTaxAmount > 0) {
    // No subtotal but has total + tax → likely VAT-inclusive
    // The total includes tax, and tax_amount was extracted from it
    rate = snapToStandardVatRate(Math.round((receiptTaxAmount / (receiptTotalAmount - receiptTaxAmount)) * 100 * 100) / 100);
    if (rate > 0 && rate <= 30) {
      taxInclusive = true;
      source = 'receipt_inclusive';
      reason = `No subtotal, tax embedded in total → VAT-inclusive at ${rate}%`;
    } else {
      rate = 15;
      taxInclusive = true;
      source = 'fallback';
      reason = `Could not determine rate from total+tax, defaulting to 15% inclusive`;
    }
  } else {
    // ── 4. Vendor-based heuristic for VAT-inclusive vs exclusive ──
    const vendorLower = vendor;
    const isVatInclusiveVendor = VAT_INCLUSIVE_KEYWORDS.some(kw => vendorLower.includes(kw));
    const isVatExclusiveVendor = VAT_EXCLUSIVE_KEYWORDS.some(kw => vendorLower.includes(kw));

    if (itemsCoverTotal) {
      // Line items already sum to the printed total — prices are
      // VAT-inclusive regardless of vendor/doc-type hints (a till slip
      // labelled "tax_invoice" is still inclusive).
      taxInclusive = true;
    } else if (isVatInclusiveVendor && !isVatExclusiveVendor) {
      taxInclusive = true;
    } else if (isVatExclusiveVendor && !isVatInclusiveVendor) {
      taxInclusive = false;
    } else {
      // Default: South African retail receipts are typically VAT-inclusive
      // B2B invoices are typically VAT-exclusive
      // If the document_type suggests it's an invoice (not a receipt), default to exclusive
      const docType = String(receiptResult?.document_type || '').toLowerCase();
      if (docType.includes('invoice') || docType.includes('delivery_note') || docType.includes('grn')) {
        taxInclusive = false;
      } else {
        taxInclusive = true;
      }
    }

    // ── 5. Currency-based default rate ──
    if (detectedCurrency === 'ZAR') {
      rate = 15;
      source = 'currency_default';
      reason = `ZAR default → 15% VAT (${taxInclusive ? 'inclusive' : 'exclusive'})`;
    } else if (input.preferredDefaultTaxRate) {
      rate = parseTaxRateValue(input.preferredDefaultTaxRate.rate);
      source = 'preferred';
      reason = `Preferred default → ${rate}% VAT (${taxInclusive ? 'inclusive' : 'exclusive'})`;
    } else {
      rate = 15;
      source = 'fallback';
      reason = `Fallback → 15% VAT (${taxInclusive ? 'inclusive' : 'exclusive'})`;
    }
  }

  return { rate, taxInclusive, source, reason };
}

/**
 * Resolve the default tax rate for an expense entry.
 *
 * Priority:
 * 1. Fuel expenses → 0% (input tax not claimable in ZA)
 * 2. Receipt has subtotal + tax_amount → compute rate from those
 * 3. Receipt has subtotal + total (implied tax) → compute rate
 * 4. Receipt tax_amount is explicitly 0 → 0%
 * 5. ZAR currency → 15% default
 * 6. Preferred default tax rate from expense accounts
 * 7. Fallback → 15%
 */
export function resolveDefaultTaxRate(input: TaxResolutionInput): TaxResolutionResult {
  const { selectedEntryType, receiptResult, detectedCurrency, preferredDefaultTaxRate } = input;

  const isFuelExpense = String(selectedEntryType).toLowerCase() === 'fuel';
  if (isFuelExpense) {
    return { rate: 0, source: 'fuel_exempt' };
  }

  const isTollExpense = String(selectedEntryType).toLowerCase() === 'toll';
  if (isTollExpense) {
    return { rate: 0, source: 'toll_exempt' };
  }

  const receiptSubtotal = receiptResult?.subtotal != null ? Number(receiptResult.subtotal) : null;
  const receiptTaxAmountRaw = receiptResult?.tax_amount != null ? Number(receiptResult.tax_amount) : null;
  const receiptTotalAmount = receiptResult?.total_amount != null ? Number(receiptResult.total_amount) : null;

  // Validate and correct the OCR-extracted tax_amount
  const receiptTaxAmount = correctReceiptTaxAmount(
    receiptTaxAmountRaw, receiptSubtotal, receiptTotalAmount, receiptResult?.raw_text,
  );

  if (receiptSubtotal && receiptSubtotal > 0 && receiptTaxAmount != null && receiptTaxAmount > 0) {
    let computedRate = Math.round((receiptTaxAmount / receiptSubtotal) * 100 * 100) / 100;
    // Check for mixed VAT treatment (blended rate) before snapping
    computedRate = resolveMixedVatRate(computedRate, receiptResult?.raw_text);
    const rate = snapToStandardVatRate(computedRate);
    if (rate <= 0 || rate > 30) {
      return { rate: 15, source: 'fallback' };
    }
    return { rate, source: 'receipt_subtotal' };
  }

  if (receiptSubtotal && receiptSubtotal > 0 && receiptTaxAmount == null && receiptTotalAmount && receiptTotalAmount > receiptSubtotal) {
    const impliedTax = receiptTotalAmount - receiptSubtotal;
    const rate = snapToStandardVatRate(Math.round((impliedTax / receiptSubtotal) * 100 * 100) / 100);
    if (rate <= 0 || rate > 30) {
      return { rate: 15, source: 'fallback' };
    }
    return { rate, source: 'receipt_implied' };
  }

  if (receiptTaxAmount === 0) {
    return { rate: 0, source: 'receipt_zero_tax' };
  }

  if (detectedCurrency === 'ZAR') {
    return { rate: 15, source: 'currency_default' };
  }

  if (preferredDefaultTaxRate) {
    return { rate: parseTaxRateValue(preferredDefaultTaxRate.rate), source: 'preferred' };
  }

  return { rate: 15, source: 'fallback' };
}
